/**
 * Meeting lifecycle routing + badge vocabulary — TASK-666.
 *
 * Moved out of `pages/MeetingsPage.tsx` (deleted) so the canvas `MeetingsList` widget and
 * the remaining meeting pages share ONE definition. `routeForMeeting` had several
 * importers, so this is a relocation, not a copy — there is exactly one lifecycle→route
 * mapping in the app.
 */

import type { MeetingListItem } from '../../services/api/meetingApi';

/** Route a meeting row to the screen matching its lifecycle stage. */
export function routeForMeeting(
  meeting: Pick<MeetingListItem, 'session_id' | 'status'>,
): string {
  const status = meeting.status.toLowerCase();
  if (status === 'live' || status === 'in_progress') return `/meeting/${meeting.session_id}/live`;
  if (status === 'completed' || status === 'post_processing') return `/meeting/${meeting.session_id}/review`;
  return `/meeting/${meeting.session_id}/prepare`;
}

export const STATUS_STYLES: Record<string, string> = {
  scheduled: 'bg-blue-100 text-blue-800',
  live: 'bg-green-100 text-green-800',
  in_progress: 'bg-green-100 text-green-800',
  post_processing: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-800',
};

export const MEETING_TYPE_LABELS: Record<string, string> = {
  advisory: 'Advisory',
  review: 'Portfolio Review',
  consultation: 'Consultation',
  onboarding: 'Onboarding',
};

/**
 * TASK-662 defines preparation, capture, analysis, documentation and review as SEPARATE
 * states. One generic "brief/record" badge conflates them, so each gets its own label +
 * short description. Every entry carries text — status is never conveyed by colour alone.
 */
export const CAPTURE_SOURCE_LABELS: Record<string, string> = {
  'browser-mic': 'Recorded in person',
  'attendee-bot': 'Recorded by bot',
  rm_dictation: 'Dictated summary',
  none: 'Not captured',
  // Deep-review 666: the backend could not read the capture source — say so, never imply
  // "not captured".
  unknown: 'Capture status unavailable',
};

export const ANALYSIS_STATE_LABELS: Record<string, string> = {
  not_started: 'No analysis',
  processing: 'Analysing…',
  completed: 'Analysed',
  partial_failure: 'Partly analysed',
  failed: 'Analysis failed',
  // Deep-review 666: the backend could not read the analysis state — say so honestly.
  unknown: 'Analysis status unavailable',
};

export const ANALYSIS_STATE_STYLES: Record<string, string> = {
  not_started: 'bg-gray-100 text-gray-600',
  processing: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  partial_failure: 'bg-amber-100 text-amber-900',
  failed: 'bg-red-100 text-red-800',
  unknown: 'bg-amber-100 text-amber-900',
};
